import { html, TemplateResult, unsafeCSS, svg, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles as componentStyles } from '../view/styles';
import { GzGaugeWebComponent } from '../../gzGauge';
import { FontSizes, FontWeights } from '../../../types/common/types';

@customElement('gz-multi-gauge')
export class GzMultiGaugeWebComponent extends GzGaugeWebComponent {
  @property({ type: Array, attribute: true, reflect: true })
  segmentPercentages: number[];

  @property({ type: Array, attribute: true, reflect: true })
  segmentColors: string[];

  @property({ type: String, attribute: true, reflect: true })
  severityText = '';

  @state()
  secondaryColors: Array<string>;

  private readonly cx = 50;
  private readonly cy = 48;
  private readonly radius = 35;

  static get styles() {
    return [...GzGaugeWebComponent.styles, unsafeCSS(`${componentStyles}`)];
  }

  updated(properties: PropertyValues): void {
    super.updated(properties);
    if (properties.has('segmentColors')) {
      this.setSegmentsColor();
    }
  }

  render() {
    this.setGaugeColors();

    return html`
      <div class="gauge">
        <div class="gauge--wrapper">
          <div class="gauge--labels-container">
            <span class="step step--1"></span>
            <span class="step step--2"></span>
            <span class="step step--3"></span>
          </div>
          <div class="gauge--value">${this.renderGaugeValue()}</div>
          ${this.hasLabels ? this.renderGraphWithLabels() : this.renderGraph()}
        </div>
      </div>
    `;
  }

  protected renderGraphWithLabels(): TemplateResult {
    return html`
      <svg class="outer-wrapper" viewBox="-6.7 -5 120 55" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="gradient" y1="71.67" x2="143.21" y2="71.67" gradientUnits="userSpaceOnUse">
            <stop offset=${this.gradientOffsets[0] + '%'} stop-color="${this.gaugeColors[0]}" />
            <stop offset=${this.gradientOffsets[1] + '%'} stop-color="${this.gaugeColors[1]}" />
            <stop offset=${this.gradientOffsets[2] + '%'} stop-color="${this.gaugeColors[2]}" />
          </linearGradient>
        </defs>
        <g fill-opacity="0" stroke-width="10">
          <path class="outer" d="M5 50a45 45 0 1 1 90 0" stroke-width="6" />
          <path
            id="gauge"
            class="gauge-progress"
            d="M5 50a45 45 0 1 1 90 0"
            stroke-width="6"
            stroke-dasharray="${this.dashArray}"
            stroke-dashoffset="${this.dashOffset}"
            stroke="url(#gradient)"
          />
        </g>
        <g>${this.backgroundArc()} ${this.generateSegments()}</g>

        <g fill-opacity="1">${this.renderGraphLabels()}</g>
      </svg>
    `;
  }

  protected renderGaugeValue(): TemplateResult {
    return this.disabled
      ? html`<span class="display-1 disabled">--</span>`
      : html`<div>
            <gz-counter id="counterRef" target="${this.value}" animations="${this.animations}"></gz-counter
            ><span>${this.percentageUnit ? this.percentageUnit : ''}</span>
          </div>
          <gz-text fontSize="${FontSizes.BODY}" fontWeight="${FontWeights.MEDIUM}" class="severity-text"
            >${this.severityText}</gz-text
          >`;
  }

  protected setSegmentsColor(): void {
    if (!this.segmentColors) {
      return;
    }

    this.secondaryColors = this.segmentColors.map(color => (color.startsWith('--') ? `var(${color})` : color));
  }

  private polarToCartesian(cx, cy, r, angle): { x: number; y: number } {
    const rad = (angle * Math.PI) / 180;

    return {
      x: cx + r * Math.cos(rad),
      y: cy + r * Math.sin(rad),
    };
  }

  private describeArc(cx, cy, r, startAngle, endAngle): string {
    const start = this.polarToCartesian(cx, cy, r, startAngle);
    const end = this.polarToCartesian(cx, cy, r, endAngle);
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
  }

  private generateSegments(): TemplateResult | undefined {
    if (!this.secondaryColors || this.disabled) {
      return;
    }

    let currentAngle = 180;
    const segments = this.segmentPercentages
      .map((percentage, index) => {
        if(percentage === 0) {
          return;
        }
        const angle = (percentage / 100) * 180;
        const d = this.describeArc(this.cx, this.cy, this.radius, currentAngle, currentAngle + angle);
        const color = this.secondaryColors[index];
        currentAngle += angle;

        return svg`<path d="${d}" stroke="${color}" fill="none" stroke-width="4" stroke-linecap="round" />`;
      })
      .reverse();

    return svg`${segments}`;
  }

  private backgroundArc(): TemplateResult {
    return svg`
      <path d="${this.describeArc(
        this.cx,
        this.cy,
        this.radius,
        -180,
        0
      )}" fill="none" stroke-width="1" class="background-arc"></path>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-multi-gauge': GzMultiGaugeWebComponent;
  }
}
